import { Skeleton } from 'antd';
import { motion } from 'framer-motion';
import './homStyle.css';

interface AdsSkeletonProps {
    displayType: 'grid' | 'list';
    count?: number;
    isIndividualCard?: boolean;
}

const SkeletonCard: React.FC<{ displayType: 'grid' | 'list' }> = ({ displayType }) => (
    <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4 }}
        className="w-full h-full bg-white rounded-lg shadow overflow-hidden shimmer ad-card"
    >
        <div className={`
            ${displayType === 'list' ? 'flex gap-4 p-4' : 'flex flex-col h-[300px]'}
        `}>
            <div className={`
                relative overflow-hidden rounded-lg bg-gray-200
                ${displayType === 'list' ? 'w-40 h-36' : 'w-full aspect-[4/3]'}
            `}>
                <div className="relative w-full h-full">
                    <Skeleton.Image className="absolute inset-0 w-full h-full object-cover" active />
                </div>

                {/* Premium badge placeholder (25% chance) */}
                {Math.random() > 0.75 && (
                    <div className="absolute top-0 right-0 bg-amber-100/80 backdrop-blur-sm text-amber-600 text-xs py-1 px-3 rounded-bl-lg">
                        <Skeleton.Button active size="small" className="w-16 h-4" />
                    </div>
                )}
            </div>

            <div className={`flex-1 flex flex-col ${displayType === 'list' ? 'w-2/3' : 'p-3'}`}>
                <div className="flex justify-between items-center mb-2">
                    <Skeleton.Button active size="small" className="w-20 h-5" />
                    <Skeleton.Avatar active size="small" shape="circle" />
                </div>

                <Skeleton.Input active size="small" className="w-4/5 mb-2" />

                <div className="flex gap-2 mb-2">
                    <Skeleton.Button active size="small" className="w-16 h-5" />
                    <Skeleton.Button active size="small" className="w-16 h-5" />
                </div>

                <Skeleton active paragraph={{ rows: displayType === 'list' ? 2 : 1 }} />

                <div className="mt-auto pt-2 flex justify-between items-center">
                    <Skeleton.Button active size="small" className="w-24 h-4" />
                    <Skeleton.Button active size="small" className="w-12 h-4" />
                </div>
            </div>
        </div>
    </motion.div>
);

const AdsSkeleton: React.FC<AdsSkeletonProps> = ({ displayType, count = 4, isIndividualCard = false }) => {
    if (isIndividualCard) {
        return <SkeletonCard displayType={displayType} />;
    }

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            className={`w-full grid ${displayType === 'list' ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4'} gap-4`}
        aria-busy="true"
    aria-label="Loading advertisements"
        >
            {Array(count).fill(null).map((_, index) => (
                <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                    className="w-full h-full"
                >
                    <SkeletonCard displayType={displayType} />
                </motion.div>
            ))}
        </motion.div>
    );
};

export default AdsSkeleton; 